(() => {
  const SLIDER_SELECTOR = '[data-product-card-slider]';
  const SWIPE_THRESHOLD = 35;
  const ANIMATION_DURATION = 350;

  class LilMiracleInfiniteProductSlider {
    constructor(slider) {
      this.slider = slider;
      this.wrapper = slider.querySelector('.swiper-wrapper');
      this.pagination = slider.querySelector('.swiper-pagination-1');

      this.slides = Array.from(
        slider.querySelectorAll('.swiper-slide')
      );

      this.logicalIndex = 0;

      this.isAnimating = false;
      this.isDragging = false;
      this.hasMoved = false;

      this.startX = 0;
      this.currentX = 0;

      /*
       * Wird nur während einer Rückwärtsbewegung verwendet.
       * Die Kopie verhindert, dass links eine leere oder
       * kurzzeitig schwarze Fläche sichtbar wird.
       */
      this.previousClone = null;
      this.previousPrepared = false;

      /*
       * Elemente und Status der beweglichen Pagination.
       */
      this.paginationTrack = null;
      this.dots = [];
      this.paginationAnimationTimeout = null;

      if (!this.wrapper || this.slides.length === 0) {
        return;
      }

      this.handlePointerDown =
        this.handlePointerDown.bind(this);

      this.handlePointerMove =
        this.handlePointerMove.bind(this);

      this.handlePointerUp =
        this.handlePointerUp.bind(this);

      this.handleClick =
        this.handleClick.bind(this);

      this.prepareSlider();
      this.createPagination();
      this.bindEvents();
      this.updateSlideClasses();
      this.updatePagination();
    }

    prepareSlider() {
      this.slider.classList.add(
        'swiper-initialized',
        'swiper-horizontal',
        'lm-infinite-slider'
      );

      this.wrapper.style.transition = 'none';
      this.wrapper.style.transform =
        'translate3d(0, 0, 0)';

      this.slides.forEach((slide, index) => {
        slide.dataset.originalIndex = String(index);
      });
    }

    createPagination() {
      if (
        !this.pagination ||
        this.slides.length <= 1
      ) {
        if (this.pagination) {
          this.pagination.remove();
        }

        return;
      }

      this.pagination.innerHTML = '';

      this.pagination.classList.add(
        'swiper-pagination-bullets',
        'swiper-pagination-horizontal',
        'lm-moving-pagination'
      );

      this.paginationTrack =
        document.createElement('div');

      this.paginationTrack.className =
        'lm-pagination-track';

      this.pagination.appendChild(
        this.paginationTrack
      );

      this.dots = [];

      /*
       * Insgesamt werden sieben Punkte erzeugt:
       *
       * Position 0:
       * unsichtbarer Reservepunkt links
       *
       * Positionen 1 bis 5:
       * fünf sichtbare Punkte
       *
       * Position 6:
       * unsichtbarer Reservepunkt rechts
       *
       * Nach jeder Bewegung wird ein äußerer Punkt
       * auf die gegenüberliegende Seite verschoben.
       * Dadurch kann die Pagination endlos gleiten.
       */
      for (let index = 0; index < 7; index += 1) {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className =
          'swiper-pagination-bullet lm-pagination-dot';

        dot.dataset.position = String(index);

        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (
            this.isAnimating ||
            !this.paginationTrack
          ) {
            return;
          }

          const currentDots = Array.from(
            this.paginationTrack.children
          );

          const visualPosition =
            currentDots.indexOf(dot);

          /*
           * Der aktive Punkt befindet sich immer
           * an der visuellen Position 3.
           */
          const relativePosition =
            visualPosition - 3;

          /*
           * Die beiden Reservepunkte an Position
           * 0 und 6 dürfen nicht angeklickt werden.
           */
          if (
            relativePosition < -2 ||
            relativePosition > 2 ||
            relativePosition === 0
          ) {
            return;
          }

          this.moveBy(relativePosition);
        });

        this.paginationTrack.appendChild(dot);
        this.dots.push(dot);
      }
    }

    applyPaginationDotClass(
      dot,
      visualPosition
    ) {
      dot.classList.remove(
        'lm-dot-hidden-prev',
        'lm-dot-far-prev',
        'lm-dot-prev',
        'lm-dot-active',
        'lm-dot-next',
        'lm-dot-far-next',
        'lm-dot-hidden-next',
        'swiper-pagination-bullet-active'
      );

      if (visualPosition === 0) {
        dot.classList.add(
          'lm-dot-hidden-prev'
        );
      } else if (visualPosition === 1) {
        dot.classList.add(
          'lm-dot-far-prev'
        );
      } else if (visualPosition === 2) {
        dot.classList.add(
          'lm-dot-prev'
        );
      } else if (visualPosition === 3) {
        dot.classList.add(
          'lm-dot-active',
          'swiper-pagination-bullet-active'
        );
      } else if (visualPosition === 4) {
        dot.classList.add(
          'lm-dot-next'
        );
      } else if (visualPosition === 5) {
        dot.classList.add(
          'lm-dot-far-next'
        );
      } else {
        dot.classList.add(
          'lm-dot-hidden-next'
        );
      }
    }

    getPaginationMovement() {
      if (
        !this.paginationTrack ||
        this.dots.length === 0
      ) {
        return 0;
      }

      const firstDot = this.dots[0];

      const dotStyles =
        window.getComputedStyle(firstDot);

      const trackStyles =
        window.getComputedStyle(
          this.paginationTrack
        );

      /*
       * Wichtig:
       * Nicht getBoundingClientRect() verwenden.
       *
       * Die äußeren Reservepunkte werden per
       * transform: scale(...) verkleinert.
       * getBoundingClientRect() würde deshalb
       * nur die optisch verkleinerte Breite messen.
       *
       * Die CSS-Breite bleibt dagegen unabhängig
       * von der Skalierung immer korrekt.
       */
      const dotWidth =
        parseFloat(dotStyles.width) || 0;

      const gap =
        parseFloat(
          trackStyles.columnGap ||
          trackStyles.gap ||
          '0'
        ) || 0;

      return dotWidth + gap;
    }
   

    startPaginationAnimation(direction) {
      if (
        !this.paginationTrack ||
        this.dots.length !== 7 ||
        direction === 0
      ) {
        return;
      }

      if (this.paginationAnimationTimeout) {
        window.clearTimeout(
          this.paginationAnimationTimeout
        );

        this.paginationAnimationTimeout = null;
      }

      const movement =
        this.getPaginationMovement();

      if (movement <= 0) {
        return;
      }

      const currentDots = Array.from(
        this.paginationTrack.children
      );

      /*
       * Die Größen der Punkte werden bereits während
       * der Bewegung an den kommenden Zustand angepasst.
       */
      currentDots.forEach((dot, index) => {
        const nextVisualPosition =
          index - direction;

        this.applyPaginationDotClass(
          dot,
          nextVisualPosition
        );
      });

      this.paginationTrack.style.transition =
        `transform ${ANIMATION_DURATION}ms ease`;

      /*
       * Die horizontale Bewegung wird zusätzlich zur
       * dauerhaften Zentrierung von -50 % ausgeführt.
       */
      if (direction > 0) {
        this.paginationTrack.style.transform =
          `translate3d(calc(-50% - ${movement}px), -50%, 0)`;
      } else {
        this.paginationTrack.style.transform =
          `translate3d(calc(-50% + ${movement}px), -50%, 0)`;
      }
    }

    finishPaginationAnimation(direction) {
      if (
        !this.paginationTrack ||
        this.dots.length !== 7 ||
        direction === 0
      ) {
        this.updatePagination();
        return;
      }

      this.paginationTrack.style.transition =
        'none';

      /*
       * Nach der sichtbaren Bewegung wird der
       * herausgelaufene Reservepunkt auf die
       * gegenüberliegende Seite verschoben.
       */
      if (direction > 0) {
        const firstDot =
          this.paginationTrack.firstElementChild;

        if (firstDot) {
          this.paginationTrack.appendChild(
            firstDot
          );
        }
      } else {
        const lastDot =
          this.paginationTrack.lastElementChild;

        if (lastDot) {
          this.paginationTrack.insertBefore(
            lastDot,
            this.paginationTrack.firstElementChild
          );
        }
      }

      /*
       * Wichtig:
       * Der Track muss wieder bei -50 % zentriert
       * werden und darf nicht auf 0 springen.
       */
      this.paginationTrack.style.transform =
        'translate3d(-50%, -50%, 0)';

      void this.paginationTrack.offsetWidth;

      this.updatePagination();
    }

    updatePagination() {
      if (
        !this.paginationTrack ||
        this.dots.length !== 7
      ) {
        return;
      }

      const currentDots = Array.from(
        this.paginationTrack.children
      );

      currentDots.forEach(
        (dot, visualPosition) => {
          const relativePosition =
            visualPosition - 3;

          const representedIndex =
            this.modulo(
              this.logicalIndex +
                relativePosition,
              this.slides.length
            );

          dot.dataset.position =
            String(visualPosition);

          dot.dataset.slideTarget =
            String(representedIndex);

          dot.setAttribute(
            'aria-label',
            `Produktbild ${
              representedIndex + 1
            } anzeigen`
          );

          dot.setAttribute(
            'aria-current',
            relativePosition === 0
              ? 'true'
              : 'false'
          );

          this.applyPaginationDotClass(
            dot,
            visualPosition
          );
        }
      );
    }

    bindEvents() {
      if (this.slides.length <= 1) {
        return;
      }

      this.slider.addEventListener(
        'pointerdown',
        this.handlePointerDown
      );

      this.slider.addEventListener(
        'pointermove',
        this.handlePointerMove
      );

      this.slider.addEventListener(
        'pointerup',
        this.handlePointerUp
      );

      this.slider.addEventListener(
        'pointercancel',
        this.handlePointerUp
      );

      this.slider.addEventListener(
        'click',
        this.handleClick,
        true
      );
    }

    handlePointerDown(event) {
      if (
        this.isAnimating ||
        event.target.closest('.lm-pagination-dot')
      ) {
        return;
      }

      if (
        event.button !== undefined &&
        event.button !== 0
      ) {
        return;
      }

      this.isDragging = true;
      this.hasMoved = false;

      this.startX = event.clientX;
      this.currentX = event.clientX;

      this.wrapper.style.transition = 'none';

      if (this.slider.setPointerCapture) {
        try {
          this.slider.setPointerCapture(
            event.pointerId
          );
        } catch (error) {
          /*
           * Pointer Capture ist nicht immer
           * verfügbar.
           */
        }
      }
    }

    handlePointerMove(event) {
      if (
        !this.isDragging ||
        this.isAnimating
      ) {
        return;
      }

      this.currentX = event.clientX;

      const difference =
        this.currentX - this.startX;

      if (Math.abs(difference) > 5) {
        this.hasMoved = true;
      }

      /*
       * Bewegung nach rechts:
       * Vorheriges Bild als temporäre Kopie
       * links einsetzen.
       */
      if (difference > 0) {
        this.preparePreviousSlide();

        this.wrapper.style.transform =
          `translate3d(calc(-100% + ${difference}px), 0, 0)`;

        return;
      }

      /*
       * Der Nutzer hat zunächst nach rechts gezogen
       * und anschließend die Richtung gewechselt.
       */
      if (this.previousPrepared) {
        this.cancelPreviousPreparation();
      }

      this.wrapper.style.transform =
        `translate3d(${difference}px, 0, 0)`;
    }

    handlePointerUp(event) {
      if (!this.isDragging) {
        return;
      }

      this.isDragging = false;

      const difference =
        this.currentX - this.startX;

      if (difference <= -SWIPE_THRESHOLD) {
        this.slideNext();
      } else if (
        difference >= SWIPE_THRESHOLD
      ) {
        this.slidePrevious();
      } else {
        this.resetPosition();
      }

      if (
        this.slider.hasPointerCapture &&
        this.slider.hasPointerCapture(
          event.pointerId
        )
      ) {
        try {
          this.slider.releasePointerCapture(
            event.pointerId
          );
        } catch (error) {
          /*
           * Pointer wurde bereits freigegeben.
           */
        }
      }

      window.setTimeout(() => {
        this.hasMoved = false;
      }, 80);
    }

    handleClick(event) {
      if (!this.hasMoved) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    /*
     * Erstellt ausschließlich für die aktuelle
     * Rückwärtsbewegung eine Kopie des letzten Bildes.
     */
    preparePreviousSlide() {
      if (
        this.previousPrepared ||
        this.isAnimating
      ) {
        return;
      }

      const lastRealSlide =
        this.wrapper.lastElementChild;

      if (!lastRealSlide) {
        return;
      }

      this.previousClone =
        lastRealSlide.cloneNode(true);

      this.previousClone.classList.add(
        'lm-temporary-previous-clone'
      );

      this.previousClone.removeAttribute('id');

      this.previousClone.setAttribute(
        'aria-hidden',
        'true'
      );

      /*
       * IDs innerhalb des Klons entfernen,
       * damit keine doppelten HTML-IDs
       * auf der Seite entstehen.
       */
      this.previousClone
        .querySelectorAll('[id]')
        .forEach((element) => {
          element.removeAttribute('id');
        });

      this.wrapper.insertBefore(
        this.previousClone,
        this.wrapper.firstElementChild
      );

      this.wrapper.style.transition = 'none';
      this.wrapper.style.transform =
        'translate3d(-100%, 0, 0)';

      /*
       * Browser zwingend neu berechnen lassen,
       * damit die folgende Bewegung nicht mit
       * der Vorbereitung zusammengefasst wird.
       */
      void this.wrapper.offsetWidth;

      this.previousPrepared = true;
    }

    /*
     * Entfernt die temporäre Vorbereitung,
     * falls der Nutzer seine Wischrichtung
     * während des Ziehens ändert.
     */
    cancelPreviousPreparation() {
      if (!this.previousPrepared) {
        return;
      }

      if (
        this.previousClone &&
        this.previousClone.parentNode ===
          this.wrapper
      ) {
        this.previousClone.remove();
      }

      this.previousClone = null;
      this.previousPrepared = false;

      this.wrapper.style.transition = 'none';
      this.wrapper.style.transform =
        'translate3d(0, 0, 0)';

      void this.wrapper.offsetWidth;
    }

    resetPosition() {
      /*
       * Rückwärtsbewegung unterhalb des
       * Schwellwerts:
       * wieder zum aktuellen Bild zurückgleiten.
       */
      if (this.previousPrepared) {
        this.wrapper.style.transition =
          `transform ${ANIMATION_DURATION}ms ease`;

        this.wrapper.style.transform =
          'translate3d(-100%, 0, 0)';

        window.setTimeout(() => {
          this.cancelPreviousPreparation();
        }, ANIMATION_DURATION);

        return;
      }

      this.wrapper.style.transition =
        `transform ${ANIMATION_DURATION}ms ease`;

      this.wrapper.style.transform =
        'translate3d(0, 0, 0)';
    }

        slideNext() {
      if (this.isAnimating) {
        return;
      }

      /*
       * Sicherheitsbereinigung, falls zuvor kurz
       * nach rechts gezogen und anschließend nach
       * links gewischt wurde.
       */
      if (this.previousPrepared) {
        this.cancelPreviousPreparation();
      }

      this.isAnimating = true;

      /*
       * Pagination gleichzeitig mit dem Bild
       * nach links bewegen.
       */
      this.startPaginationAnimation(1);

      this.wrapper.style.transition =
        `transform ${ANIMATION_DURATION}ms ease`;

      this.wrapper.style.transform =
        'translate3d(-100%, 0, 0)';

      window.setTimeout(() => {
        const firstSlide =
          this.wrapper.firstElementChild;

        if (firstSlide) {
          this.wrapper.appendChild(firstSlide);
        }

        this.logicalIndex =
          this.modulo(
            this.logicalIndex + 1,
            this.slides.length
          );

        this.wrapper.style.transition = 'none';
        this.wrapper.style.transform =
          'translate3d(0, 0, 0)';

        this.updateSlideClasses();

        /*
         * Punktreihe nach der Animation
         * unsichtbar neu sortieren.
         */
        this.finishPaginationAnimation(1);

        void this.wrapper.offsetWidth;

        requestAnimationFrame(() => {
          this.isAnimating = false;
        });
      }, ANIMATION_DURATION);
    }

    slidePrevious() {
      if (this.isAnimating) {
        return;
      }

      /*
       * Bei einem Klick auf einen linken Punkt
       * existiert noch keine vorbereitete Kopie.
       * Deshalb wird sie vor dem Aktivieren des
       * Animationsstatus erzeugt.
       */
      if (!this.previousPrepared) {
        this.preparePreviousSlide();
      }

      if (!this.previousPrepared) {
        return;
      }

      this.isAnimating = true;

      /*
       * Pagination gleichzeitig mit dem Bild
       * nach rechts bewegen.
       */
      this.startPaginationAnimation(-1);

      /*
       * Von der aktuellen Position zur linken Kopie
       * animieren. Erst nach der Animation wird das
       * echte letzte Bild nach vorne verschoben.
       */
      this.wrapper.style.transition =
        `transform ${ANIMATION_DURATION}ms ease`;

      this.wrapper.style.transform =
        'translate3d(0, 0, 0)';

      window.setTimeout(() => {
        const lastRealSlide =
          this.wrapper.lastElementChild;

        /*
         * Temporäre Kopie entfernen.
         */
        if (
          this.previousClone &&
          this.previousClone.parentNode ===
            this.wrapper
        ) {
          this.previousClone.remove();
        }

        /*
         * Das echte letzte Bild an die erste
         * Position verschieben.
         */
        if (lastRealSlide) {
          this.wrapper.insertBefore(
            lastRealSlide,
            this.wrapper.firstElementChild
          );
        }

        this.previousClone = null;
        this.previousPrepared = false;

        this.logicalIndex =
          this.modulo(
            this.logicalIndex - 1,
            this.slides.length
          );

        this.wrapper.style.transition = 'none';
        this.wrapper.style.transform =
          'translate3d(0, 0, 0)';

        this.updateSlideClasses();

        /*
         * Punktreihe nach der Animation
         * unsichtbar neu sortieren.
         */
        this.finishPaginationAnimation(-1);

        void this.wrapper.offsetWidth;

        requestAnimationFrame(() => {
          this.isAnimating = false;
        });
      }, ANIMATION_DURATION);
    }

    moveBy(relativePosition) {
      if (
        this.isAnimating ||
        relativePosition === 0
      ) {
        return;
      }

      const direction =
        relativePosition > 0 ? 1 : -1;

      const steps =
        Math.abs(relativePosition);

      this.runMultipleSteps(
        direction,
        steps
      );
    }

    runMultipleSteps(
      direction,
      remainingSteps
    ) {
      if (remainingSteps <= 0) {
        return;
      }

      if (direction > 0) {
        this.slideNext();
      } else {
        this.slidePrevious();
      }

      if (remainingSteps > 1) {
        window.setTimeout(() => {
          this.runMultipleSteps(
            direction,
            remainingSteps - 1
          );
        }, ANIMATION_DURATION + 30);
      }
    }

    updateSlideClasses() {
      const currentSlides = Array.from(
        this.wrapper.children
      ).filter((slide) => {
        return !slide.classList.contains(
          'lm-temporary-previous-clone'
        );
      });

      currentSlides.forEach(
        (slide, index) => {
          const isActive = index === 0;

          slide.classList.toggle(
            'swiper-slide-active',
            isActive
          );

          slide.classList.toggle(
            'swiper-slide-next',
            index === 1
          );

          slide.classList.toggle(
            'swiper-slide-prev',
            index ===
              currentSlides.length - 1
          );

          slide.setAttribute(
            'aria-hidden',
            isActive ? 'false' : 'true'
          );
        }
      );
    }

    modulo(value, divisor) {
      return (
        ((value % divisor) + divisor) %
        divisor
      );
    }
  }

  function initializeSlider(slider) {
    if (
      !slider ||
      slider.dataset.lmSliderInitialized ===
        'true'
    ) {
      return;
    }

    slider.dataset.lmSliderInitialized =
      'true';

    new LilMiracleInfiniteProductSlider(
      slider
    );
  }

  function initializeAll(scope = document) {
    if (
      scope instanceof HTMLElement &&
      scope.matches(SLIDER_SELECTOR)
    ) {
      initializeSlider(scope);
    }

    scope
      .querySelectorAll?.(SLIDER_SELECTOR)
      .forEach(initializeSlider);
  }

  function start() {
    initializeAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  /*
   * Slider neu initialisieren, wenn eine
   * Shopify-Section im Theme-Editor neu
   * geladen wird.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAll(event.target);
    }
  );

  /*
   * Dynamisch nachgeladene Produktkarten
   * erkennen und automatisch initialisieren.
   */
  const observer = new MutationObserver(
    (mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(
          (node) => {
            if (
              !(node instanceof HTMLElement)
            ) {
              return;
            }

            initializeAll(node);
          }
        );
      });
    }
  );

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();